import {
  type AdminEndpointKey,
  type AdminApiError,
  getEndpointMeta,
  expandPath,
} from "./admin-registry";
import { useAdminStore } from "@/stores/admin-store";
import type { ApiLogEntry, ApiHealthStatus } from "@/stores/admin-store";
import { getLogtoToken } from "@/services/auth/token-provider";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminRequestOptions {
  params?: Record<string, string>;
  query?: Record<string, string>;
  body?: unknown;
  isSuperadmin?: boolean;
}

export interface AdminRequestResult<T> {
  data: T;
  httpStatus: number;
  correlationId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildAdminApiError(
  kind: AdminApiError["kind"],
  httpStatus: number,
  endpointKey: AdminEndpointKey,
  details?: string,
  correlationId?: string,
): AdminApiError {
  return { kind, httpStatus, endpointKey, correlationId, details };
}

function mapHttpStatusToKind(status: number): AdminApiError["kind"] {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 422) return "validation";
  if (status === 429) return "rate_limit";
  return "server";
}

function deriveHealthStatus(httpStatus: number): ApiHealthStatus {
  if (httpStatus >= 200 && httpStatus < 400) return "live";
  if (httpStatus === 429 || httpStatus === 503) return "degraded";
  return "offline";
}

function generateLogId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ---------------------------------------------------------------------------
// API Base URL
// ---------------------------------------------------------------------------

function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_URL || "https://api.lexe.pro/api/v1";
}

// ---------------------------------------------------------------------------
// Main wrapper — fetch() diretto, no ApiClient
// ---------------------------------------------------------------------------

export async function adminRequest<T = unknown>(
  key: AdminEndpointKey,
  opts: AdminRequestOptions = {},
): Promise<AdminRequestResult<T>> {
  const meta = getEndpointMeta(key);
  const store = useAdminStore.getState();

  // -- Superadmin fail-closed gate --
  if (
    opts.isSuperadmin &&
    (meta.data_class === "customer_pii" || meta.data_class === "customer_content")
  ) {
    throw buildAdminApiError(
      "superadmin_policy",
      403,
      key,
      "Policy superadmin: usa endpoint metrics",
    );
  }

  // -- Build URL --
  const expandedPath = expandPath(meta.path, opts.params);
  const baseUrl = getApiBaseUrl();
  let url = `${baseUrl}${expandedPath}`;
  if (opts.query) {
    const qs = new URLSearchParams(opts.query).toString();
    if (qs) url += `?${qs}`;
  }

  // -- Correlation ID for mutative methods --
  let correlationId: string | undefined;
  const isMutative = meta.method !== "GET";
  if (isMutative) {
    correlationId = crypto.randomUUID();
  }

  // -- Build headers --
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (correlationId) {
    headers["X-Request-Id"] = correlationId;
  }

  // Logto token
  const token = await getLogtoToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // -- Execute request --
  const startMs = performance.now();
  let httpStatus = 0;

  try {
    const response = await fetch(url, {
      method: meta.method,
      headers,
      body: isMutative && opts.body ? JSON.stringify(opts.body) : undefined,
    });

    httpStatus = response.status;
    const durationMs = Math.round(performance.now() - startMs);

    // -- 401 interceptor: clean logout --
    if (httpStatus === 401) {
      const logEntry: ApiLogEntry = {
        id: generateLogId(),
        endpointKey: key,
        method: meta.method,
        path: expandedPath,
        httpStatus,
        durationMs,
        timestamp: Date.now(),
        correlationId,
        error: "Unauthorized",
      };
      store.pushApiLog(logEntry);
      store.upsertApiStatus(key, {
        status: "offline",
        lastCheckAt: Date.now(),
        lastHttpStatus: httpStatus,
        note: "Session expired",
      });

      // Redirect to login — let AuthGuard handle the cleanup
      window.location.href = "/session-expired";

      throw buildAdminApiError("auth", 401, key, "Session expired", correlationId);
    }

    // -- Log --
    const logEntry: ApiLogEntry = {
      id: generateLogId(),
      endpointKey: key,
      method: meta.method,
      path: expandedPath,
      httpStatus,
      durationMs,
      timestamp: Date.now(),
      correlationId,
    };

    if (!response.ok) {
      let errorMessage = `HTTP ${httpStatus}`;
      try {
        const errorBody = await response.json();
        const rawDetail = errorBody.detail;
        errorMessage =
          typeof rawDetail === "string"
            ? rawDetail
            : (rawDetail?.message ?? errorBody.message ?? errorMessage);
      } catch {
        // ignore JSON parse errors
      }

      logEntry.error = errorMessage;
      store.pushApiLog(logEntry);
      store.upsertApiStatus(key, {
        status: deriveHealthStatus(httpStatus),
        lastCheckAt: Date.now(),
        lastHttpStatus: httpStatus,
        note: errorMessage,
      });

      throw buildAdminApiError(
        mapHttpStatusToKind(httpStatus),
        httpStatus,
        key,
        errorMessage,
        correlationId,
      );
    }

    // 204 No Content — success with no body (e.g. DELETE)
    if (response.status === 204) {
      store.pushApiLog(logEntry);
      store.upsertApiStatus(key, {
        status: deriveHealthStatus(httpStatus),
        lastCheckAt: Date.now(),
        lastHttpStatus: httpStatus,
      });
      return { data: null as T, httpStatus, correlationId };
    }

    const data = await response.json();

    store.pushApiLog(logEntry);
    store.upsertApiStatus(key, {
      status: deriveHealthStatus(httpStatus),
      lastCheckAt: Date.now(),
      lastHttpStatus: httpStatus,
    });

    // -- Schema validation --
    if (meta.responseSchema) {
      const parsed = meta.responseSchema.safeParse(data);
      if (!parsed.success) {
        throw buildAdminApiError(
          "schema",
          httpStatus,
          key,
          `Schema validation failed: ${parsed.error.issues.map((i) => i.message).join(", ")}`,
          correlationId,
        );
      }
      return { data: parsed.data as T, httpStatus, correlationId };
    }

    return { data, httpStatus, correlationId };
  } catch (err: unknown) {
    const durationMs = Math.round(performance.now() - startMs);

    // Already an AdminApiError
    if (err && typeof err === "object" && "kind" in err && "endpointKey" in err) {
      throw err;
    }

    // Network / unknown error
    const logEntry: ApiLogEntry = {
      id: generateLogId(),
      endpointKey: key,
      method: meta.method,
      path: expandedPath,
      httpStatus: 0,
      durationMs,
      timestamp: Date.now(),
      correlationId,
      error: err instanceof Error ? err.message : "Unknown error",
    };
    store.pushApiLog(logEntry);
    store.upsertApiStatus(key, {
      status: "offline",
      lastCheckAt: Date.now(),
      lastHttpStatus: 0,
      note: "Network error",
    });

    throw buildAdminApiError(
      "network",
      0,
      key,
      err instanceof Error ? err.message : "Network error",
      correlationId,
    );
  }
}
